import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import {
  constructV9QueryParams,
  getDefaultDistance
} from 'Src/behaviors/search-v9/actions/utils';
import {
  fetchProvidersV9,
  fetchProviderSlotCounts,
  toggleMobileFacets,
  getUpdatedSearch as baseGetUpdatedSearch,
  searchSelector,
  getProviderIds,
  querySelector,
  updateSearchQuery
} from '../behaviors/search-v9';
import {
  setPatientRel,
  setPurpose,
  resetAvailability,
  fetchGlobalPurposesByPatientRelV9,
  fetchPurposesByProviderId,
  fetchSlots,
  fetchProviderAvailability
} from '../behaviors/availability';
import { isError } from '../behaviors/error';
import { isLoading } from '../behaviors/loading';
import { isSlotsCountLoading } from '../behaviors/slotsCountLoading';
import { getProvidersByIds } from '../behaviors/providers';
import { configSelector } from '../behaviors/configuration';
import { settingsSelector } from '../behaviors/settings/selectors';
import { pages } from '../tracking/constants';
import { decorateSearchTracking } from './logging-decorators';
import { pageViewEvent } from '../tracking/tracking-utils';
import { useDemographicsLocation } from '../hooks';
import { useMigrateV8SearchParams } from './hooks/migrate-v8-search-params';
import SearchV9 from '../search-v9';
import { productNameSelector } from '../behaviors/product-name';
import { isUnrefinedSearch } from 'Src/utils/search-common';

const mapStateToProps = (state, ownProps) => {
  const config = configSelector(state);
  const appSettings = settingsSelector(state);

  const {
    direct_book: {
      patient_type: { buttons: availabilityButtonsConfig } = {}
    } = {}
  } = config;

  const log = (message, event_data = {}) => {
    const additionalTracking = decorateSearchTracking();

    ownProps.log(message, { ...event_data, ...additionalTracking });
  };

  const distanceSort = _get(
    config,
    'location_facet.custom_sort_order',
    'distance'
  );

  const providers = getProvidersByIds(state, getProviderIds(state)); // we could use reselect for this.
  const loading = isLoading(state);
  const slotsCountLoading = isSlotsCountLoading(state);
  const error = isError(state);

  let apptInfo = {
    loading: state.availability.loading,
    purposeOptions: state.availability.purposeOptions
  };
  if (
    state.availability &&
    state.availability.patientRel &&
    state.availability.purpose
  ) {
    apptInfo = {
      ...apptInfo,
      relationship: state.availability.patientRel,
      purpose: state.availability.purpose
    };
  }

  const currentQuery = querySelector(state);
  const currentLocation = ownProps.location;

  const getUpdatedSearch = (modifications = []) => {
    return baseGetUpdatedSearch(
      config,
      currentQuery,
      currentLocation,
      modifications
    );
  };

  return {
    customerCode: state.customerCode,
    productName: productNameSelector(state),
    config,
    appSettings,
    log,
    distanceSort,
    providers,
    loading,
    slotsCountLoading,
    getUpdatedSearch,
    error,
    apptInfo,
    state,
    availabilityButtonsConfig,
    searchProviderIds: state.searchV9.providerIds,
    patientSearchRefiner: state.searchV9.patientSearchRefiner,
    slotsByProviderId: state.availability.slotsByProviderId,
    slotsStatusByProviderId: state.availability.slotsStatusByProviderId,
    hasFetchingSlotCountsFailed: state.availability.hasFetchingSlotCountsFailed,
    purposeOptionsByProviderId: state.availability.purposeOptionsByProviderId,
    availablePurposesByProviderId:
      state.availability.availablePurposesByProviderId,
    availabilityError: state.availability.error,
    purposesLoading: state.availability.purposesLoading,
    // server side fetch function
    serverSideExecute: async ({ props, req, _res, _store }) => {
      // this.selector.props are the props collected from `connect` and `mapStateToProps`
      // We need to access these instead of `this.props`
      const { fetchProvidersV9, config, location } = props;

      if (config?.third_party?.cerner_healthelife) {
        // disable search SSR for cerner integrations to allow auto-zip to work, which only works in client.
        return;
      }

      await fetchProvidersV9(location, { req });

      // uncomment to enable server-side slot rendering. Disabled for now to keep parity with what
      // existing flask SSR is doing. Enabling here will increase initial page load time for however long slots take.
      // await fetchProviderSlotCounts(location, { req });
    },
    ...searchSelector(state)
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      fetchProvidersV9,
      fetchProviderSlotCounts,
      toggleMobileFacets,
      setPatientRel,
      setPurpose,
      resetAvailability,
      fetchGlobalPurposesByPatientRelV9,
      fetchPurposesByProviderId,
      fetchSlots,
      fetchProviderAvailability,
      updateSearchQuery
    },
    dispatch
  );
};

function SearchContainerComponentV9(props) {
  const {
    customerCode,
    searchSummary,
    location,
    log,
    fetchProvidersV9,
    fetchProviderSlotCounts,
    config,
    getUpdatedSearch,
    history,
    state,
    searchCriteria,
    updateSearchQuery
  } = props;
  const [previousSearchCriteria, setPreviousSearchCriteria] =
    useState(searchCriteria);

  // Use unified hook that handles both CCF and SMART on FHIR integrations
  // Only makes API calls for the enabled integration
  const { patientZip, patientZipSearchLocation, patientZipPending } =
    useDemographicsLocation({
      config,
      customerCode
    });

  const [isUserLocationFetched, setIsUserLocationFetched] =
    useState(patientZipPending);

  const { completed: areV8ParamsChecked } = useMigrateV8SearchParams({
    location,
    history
  });

  const hasCompletedPreprocessing = isUserLocationFetched && areV8ParamsChecked;

  useEffect(() => {
    if (patientZipPending) {
      // don't do anything until pre-processing is complete
      return;
    }

    if (patientZipSearchLocation && !searchSummary.location) {
      // if we have an auto-geolocated zip code from demographics (CCF or SMART on FHIR),
      // and we're not already searching for a location, update the search to use that zip
      history.push(
        getUpdatedSearch([
          {
            action: 'append',
            key: 'location',
            value: patientZipSearchLocation
          },
          {
            action: 'append',
            key: 'display_location',
            value: patientZip
          },
          {
            action: 'append',
            key: 'distance',
            value: getDefaultDistance(state)
          }
        ])
      );
    }
    setIsUserLocationFetched(true);
    // remove searchSummary.location from deps array to allow totally clearing filters even in ccf
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientZipPending, patientZipSearchLocation, searchSummary.location]);

  useEffect(() => {
    async function doFetch() {
      // if the current search is already in the state, don't reload.
      // This is true for server side rendering and coming back from profile pages.
      if (!hasCompletedPreprocessing) {
        // don't do anything until pre-processing is complete
        return;
      }

      const nextSearchCriteria = constructV9QueryParams(location, state);
      if (isUnrefinedSearch(nextSearchCriteria)) {
        // update the search summary since we aren't triggering a new search where the search summary is also updated
        const currentQuery = querySelector(state);
        updateSearchQuery({
          searchSummary: {
            ...searchSummary,
            patient_search_refiner: 'false',
            query_string: location.search
          },
          query: {
            ...currentQuery,
            patient_search_refiner: 'false'
          }
        });
        // don't fetch for unrefined searches
        return;
      }

      if (!_isEqual(nextSearchCriteria, previousSearchCriteria)) {
        await fetchProvidersV9(location);

        log('user_action.search_results.view_search_results', searchSummary);

        setPreviousSearchCriteria(nextSearchCriteria);
      }

      await fetchProviderSlotCounts(location);
    }

    doFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, hasCompletedPreprocessing]);

  useEffect(() => {
    if (state.searchV9.source === 'server') {
      log('user_action.search_results.view_search_results', searchSummary);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.searchV9.source]);

  useEffect(() => {
    // log page view on first load
    log(pageViewEvent(pages.SEARCH_RESULTS));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!previousSearchCriteria?.location || !searchCriteria?.location) {
      return;
    }

    if (previousSearchCriteria.location !== searchCriteria.location) {
      history.push(
        getUpdatedSearch([
          {
            action: 'append',
            key: 'location',
            value: searchCriteria.location
          }
        ])
      );
      setPreviousSearchCriteria(searchCriteria);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUpdatedSearch, previousSearchCriteria]);

  if (config.darkship_feature_flags?.fix_4x_search_issue ?? true) {
    // eslint-disable-next-line no-unused-vars
    const { serverSideExecute, ...modifiedProps } = props;

    return <SearchV9 {...modifiedProps} />;
  } else {
    // note: This change is a revert of HSAP-1647 which fixed an issue where the server side
    // rendering function serverSideExecute was executed 4 times in total.
    // The way the issue comes about is the props spread on this line transfers the serverSideExecute
    // function in this file to the SearchV9 component as well as the wrapper component for intl
    // and the router. A function named ssrPrepass traverses the
    // component tree and executes a callback that looks for functions on props with the name
    // serverSideExecute. When props are spread in this way react transfers the props and ssrPrepass calls the
    // function on the hidden wrapper components.

    return <SearchV9 {...props} />;
  }
}

const SearchContainerV9 = connect(
  mapStateToProps,
  mapDispatchToProps
)(SearchContainerComponentV9);

export default SearchContainerV9;
